import { useDebounce } from "@utils/utils";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { faqData } from "./faqData";

const FAQPage = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleSection, setVisibleSection] = useState(Object.keys(faqData)[0]);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const sectionRefs = useRef({});
  const observerRef = useRef(null);

  const toggleAccordion = (category, index) => {
    setActiveIndex((prev) => (prev === `${category}-${index}` ? null : `${category}-${index}`));
  };

  const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const extractTextFromJSX = (element) => {
    if (typeof element === "string") {
      return element;
    }

    if (typeof element === "number") {
      return element.toString();
    }

    if (React.isValidElement(element)) {
      if (typeof element.props.children === "string") {
        return element.props.children;
      }

      if (Array.isArray(element.props.children)) {
        return element.props.children.map((child) => extractTextFromJSX(child)).join(" ");
      }

      if (element.props.children) {
        return extractTextFromJSX(element.props.children);
      }
    }

    if (Array.isArray(element)) {
      return element.map((item) => extractTextFromJSX(item)).join(" ");
    }

    return "";
  };

  // Enhanced function to get searchable text from answer
  const getSearchableText = (answer) => {
    if (typeof answer === "string") {
      return answer;
    }

    // For JSX/React components, extract text content
    return extractTextFromJSX(answer);
  };

  const highlightText = (text, highlight) => {
    if (!highlight) return text;
    const escaped = escapeRegExp(highlight);
    return text.split(new RegExp(`(${escaped})`, "gi")).map((part, idx) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={idx} className="tw-bg-yellow-200 tw-font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // Enhanced function to highlight text within JSX elements
  const highlightInJSX = (element, highlight) => {
    if (!highlight || !highlight.trim()) return element;

    const escaped = escapeRegExp(highlight.trim());
    const regex = new RegExp(`(${escaped})`, "gi");

    const highlightTextInElement = (el) => {
      if (typeof el === "string") {
        // Split and highlight text
        return el.split(regex).map((part, idx) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <span key={idx} className="tw-bg-yellow-200 tw-font-semibold">
              {part}
            </span>
          ) : (
            part
          )
        );
      }

      if (typeof el === "number") {
        return el;
      }

      if (React.isValidElement(el)) {
        // Clone the element with highlighted children
        const newProps = { ...el.props };

        if (el.props.children) {
          if (Array.isArray(el.props.children)) {
            newProps.children = el.props.children.map((child, idx) => <React.Fragment key={idx}>{highlightTextInElement(child)}</React.Fragment>);
          } else {
            newProps.children = highlightTextInElement(el.props.children);
          }
        }

        return React.cloneElement(el, newProps);
      }

      if (Array.isArray(el)) {
        return el.map((item, idx) => <React.Fragment key={idx}>{highlightTextInElement(item)}</React.Fragment>);
      }

      return el;
    };

    return highlightTextInElement(element);
  };

  const filteredFaqData = useMemo(() => {
    if (!debouncedSearchTerm || !debouncedSearchTerm.trim()) return faqData;
    const lowerSearch = debouncedSearchTerm.toLowerCase();
    const filtered = {};

    Object.keys(faqData).forEach((category) => {
      const items = faqData[category].filter((item) => {
        // Search in question
        const questionMatch = item.question.toLowerCase().includes(lowerSearch);
        const answerText = getSearchableText(item.answer);
        const answerMatch = answerText.toLowerCase().includes(lowerSearch);

        return questionMatch || answerMatch;
      });

      if (items.length > 0) {
        filtered[category] = items;
      }
    });
    return filtered;
  }, [debouncedSearchTerm]);

  const hasResults = Object.keys(filteredFaqData).length > 0;

  useEffect(() => {
    if (hasResults) {
      const firstCategory = Object.keys(filteredFaqData)[0];
      if (firstCategory && filteredFaqData[firstCategory].length > 0) {
        const firstItemKey = `${firstCategory}-0`;
        setActiveIndex(firstItemKey);
      }
    } else {
      setActiveIndex(null);
    }
  }, [filteredFaqData, hasResults]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((e) => e.isIntersecting);
        if (visibleEntries.length > 0) {
          const topMost = visibleEntries.reduce((prev, curr) => (prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr));
          if (topMost.target.id) {
            setVisibleSection(topMost.target.id);
          }
        }
      },
      {
        threshold: 0.3,
        rootMargin: "-50px 0px -50px 0px",
      }
    );

    const currentCategories = Object.keys(filteredFaqData);
    currentCategories.forEach((category) => {
      const el = sectionRefs.current[category];
      if (el && observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    if (currentCategories.length > 0 && !currentCategories.includes(visibleSection)) {
      setVisibleSection(currentCategories[0]);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [filteredFaqData, visibleSection]);

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <div className="tw-max-w-[1440px] tw-mx-auto tw-text-gray-900 tw-p-4">
        {/* Header + Search */}
        <div className="tw-flex tw-flex-col tw-items-center">
          <div className="tw-text-base tw-font-medium">Frequently Asked Questions</div>
          <div className="tw-relative tw-w-2/5 tw-mt-3">
            <input type="text" className="tw-w-full tw-rounded-full tw-border tw-py-2 tw-pl-10 tw-pr-4 tw-text-sm tw-h-9 tw-shadow-button_white tw-border-gray-200 tw-outline-none focus:tw-border-primary-purple-500 focus:tw-ring-primary-purple-500" placeholder="Search FAQs" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            <span className="tw-absolute tw-left-3 tw-top-2">
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                <path d="M781.69-136.92 530.46-388.16q-30 24.77-69 38.77-39 14-80.69 14-102.55 0-173.58-71.01-71.03-71.01-71.03-173.54 0-102.52 71.01-173.6 71.01-71.07 173.54-71.07 102.52 0 173.6 71.03 71.07 71.03 71.07 173.58 0 42.85-14.38 81.85-14.39 39-38.39 67.84l251.23 251.23-42.15 42.16ZM380.77-395.38q77.31 0 130.96-53.66 53.66-53.65 53.66-130.96t-53.66-130.96q-53.65-53.66-130.96-53.66t-130.96 53.66Q196.15-657.31 196.15-580t53.66 130.96q53.65 53.66 130.96 53.66Z" />
              </svg>
            </span>
          </div>
        </div>

        <div className="tw-flex tw-gap-5 tw-mt-6">
          <div className="tw-flex-1 tw-bg-white tw-p-3 tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden tw-max-h-[calc(100vh-170px)] tw-overflow-y-auto no-scrollbar">
            {hasResults ? (
              Object.keys(filteredFaqData).map((category) => {
                const items = filteredFaqData[category];
                return (
                  <div
                    key={category}
                    id={category}
                    ref={(el) => {
                      sectionRefs.current[category] = el;
                    }}>
                    <div className="tw-text-base tw-font-semibold tw-text-gray-900 tw-py-2">{category}</div>
                    <div className="tw-divide-y tw-divide-gray-200">
                      {items.map((item, index) => {
                        const isActive = activeIndex === `${category}-${index}`;
                        return (
                          <div key={item.question}>
                            <button onClick={() => toggleAccordion(category, index)} className="tw-w-full tw-px-2 tw-py-3 tw-text-left tw-flex tw-justify-between tw-items-center tw-transition-all tw-duration-200 tw-bg-white" aria-expanded={isActive}>
                              <span className="tw-font-medium tw-text-sm tw-pr-4">{highlightText(item.question, debouncedSearchTerm.trim())}</span>
                              <span className="material-symbols-outlined tw-text-lg">{isActive ? "do_not_disturb_on" : "add_circle"}</span>
                            </button>
                            <div className={`tw-overflow-hidden tw-transition-all tw-duration-300 ${isActive ? "tw-max-h-96 tw-opacity-100" : "tw-max-h-0 tw-opacity-0"}`}>
                              <div className="tw-px-3 tw-bg-white tw-text-gray-700 tw-text-sm tw-leading-relaxed tw-pb-3">{typeof item.answer === "string" ? highlightText(item.answer, debouncedSearchTerm.trim()) : highlightInJSX(item.answer, debouncedSearchTerm.trim())}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20">
                <div className="tw-text-gray-400 tw-mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 -960 960 960" width="48px" fill="currentColor">
                    <path d="M781.69-136.92 530.46-388.16q-30 24.77-69 38.77-39 14-80.69 14-102.55 0-173.58-71.01-71.03-71.01-71.03-173.54 0-102.52 71.01-173.6 71.01-71.07 173.54-71.07 102.52 0 173.6 71.03 71.07 71.03 71.07 173.58 0 42.85-14.38 81.85-14.39 39-38.39 67.84l251.23 251.23-42.15 42.16ZM380.77-395.38q77.31 0 130.96-53.66 53.66-53.65 53.66-130.96t-53.66-130.96q-53.65-53.66-130.96-53.66t-130.96 53.66Q196.15-657.31 196.15-580t53.66 130.96q53.65 53.66 130.96 53.66Z" />
                  </svg>
                </div>
                <div className="tw-text-lg tw-font-medium tw-text-gray-600 tw-mb-2">No FAQs found</div>
                <div className="tw-text-sm tw-text-gray-500 tw-text-center">Try adjusting your search terms or browse all available FAQs</div>
                <button onClick={() => setSearchTerm("")} className="tw-mt-4 tw-px-4 tw-py-2 tw-text-sm tw-text-primary-purple-500 tw-border tw-border-primary-purple-500 tw-rounded-full hover:tw-bg-primary-purple-50 tw-transition-colors">
                  Clear search
                </button>
              </div>
            )}
          </div>

          <div className="tw-w-[250px] tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-p-4">
            <div className="tw-text-xs tw-font-semibold tw-text-gray-900 tw-mb-4">FAQs categories</div>
            {hasResults ? (
              <div className="tw-flex tw-flex-col tw-pl-2">
                {Object.keys(filteredFaqData).map((category) => (
                  <button key={category} onClick={() => scrollToSection(category)} className={`tw-bg-white tw-text-left tw-text-sm tw-py-1.5 tw-ps-2.5 tw-transition-all tw-duration-100 tw-border-l-2 ${visibleSection === category ? "tw-text-primary-purple-500 tw-font-semibold tw-border-primary-purple-500" : "tw-text-gray-700 tw-border-gray-300"}`}>
                    {category}
                  </button>
                ))}
              </div>
            ) : (
              <div className="tw-text-xs tw-text-gray-600">No matching categories.</div>
            )}
            <div className="tw-text-xs tw-text-gray-900 tw-mt-6">
              If you have any query reach out to&nbsp;
              <a href="mailto:support@trademo.com" className="tw-text-primary-purple-500 hover:tw-text-primary-purple-500 !tw-underline">
                support@trademo.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQPage;
